import React from 'react';
import { Warning } from '@phosphor-icons/react';
import HelpApiLayout from '../../../../components/layout/HelpApiLayout';
import HelpSidebar from '../../../../components/layout/HelpSidebar';
import ArticleNavigation from '../../../../components/layout/docs/ArticleNavigation';
import LastUpdated from '../../../../components/layout/docs/LastUpdated';
import { getHelpArticleRoute } from '../../../../constants/routes';
import { getHelpRoute } from '../../../../utils/navigation';
import '../getting-started/HelpCategoryPage.css';

/**
 * Request (P2P) Article Page
 */
const RequestP2P = () => {
  return (
    <HelpApiLayout type="help" sidebar={<HelpSidebar />}>
      <div className="help-category-page">
        <div className="help-category-container">
          <main className="help-category-main">
            <div className="article-content">
              <header className="article-header">
                <h1 className="article-title">Request (P2P)</h1>
                <p className="article-subtitle">
                  Learn how to ask for a reimbursement or payment from a friend directly within the app.
                </p>
              </header>

              <div className="article-separator"></div>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Overview</h2>
                  <p className="article-section-intro">
                    Need to get paid back for a coffee or a shared bill? WeSplit allows you to send a payment request to any other user. They will receive a notification and can settle the debt in one click.
                  </p>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Method 1: From the Dashboard</h2>
                  <p className="article-section-intro">
                    Start a request from the main screen.
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Tap Request:</strong> Click on the "Request" button (usually represented by a plus or arrow icon) on your dashboard.
                    </li>
                    <li>
                      <strong>Select Contact:</strong>
                      <ul className="article-list-dots">
                        <li>Choose the friend you want to ask for money.</li>
                      </ul>
                      <div className="article-note" style={{ marginTop: '8px' }}>
                        <p>Note: You can easily search by name or username.</p>
                      </div>
                    </li>
                    <li>
                      <strong>Enter Amount:</strong> Input the specific amount of USDC you are owed.
                    </li>
                    <li>
                      <strong>Add a Message (Optional):</strong> Add a note to remind them what this is for (e.g., "Lunch yesterday 🍕").
                    </li>
                    <li>
                      <strong>Send Request:</strong> Confirm to notify the user.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Method 2: From a Profile Page</h2>
                  <p className="article-section-intro">
                    If you are viewing a friend's profile, you can initiate the request immediately.
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Visit Profile:</strong> Tap on a user's name from your contact list or transaction history.
                    </li>
                    <li>
                      <strong>Quick Action:</strong> Tap the "Request" shortcut button on their profile.
                    </li>
                    <li>
                      <strong>Enter Details:</strong> Input the amount and an optional message.
                    </li>
                    <li>
                      <strong>Confirm:</strong> The request is sent instantly.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section">
                <div className="article-warning">
                  <div className="article-warning-header">
                    <Warning className="article-warning-icon" weight="fill" />
                    <h3 className="article-warning-title">Important Limitations</h3>
                  </div>
                  <div className="article-warning-content">
                    <ul className="article-list-dots">
                      <li>
                        <strong>Internal Users Only:</strong> Unlike the "Send" feature, you <strong>cannot</strong> send a request to a linked external wallet or card. Requests are strictly for interactions between WeSplit users.
                      </li>
                      <li>
                        <strong>Flexible Flow:</strong> If you selected the wrong contact, you can change who you are requesting money from during the process without losing your entered amount.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <div className="article-separator"></div>

              <LastUpdated />

              <ArticleNavigation
                previous={{
                  title: 'Send (P2P)',
                  route: getHelpRoute(getHelpArticleRoute('features', 'send-p2p'))
                }}
                next={{
                  title: 'Gas Fees',
                  route: getHelpRoute(getHelpArticleRoute('features', 'gas-fees'))
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default RequestP2P;
